import React, { useEffect, useState, useContext, useRef, useLayoutEffect } from "react";
import { CombinedContext } from "../../../context/combinedContext";
import translations from "../../../translations/translations";
import companyBasedRequest from "../../../request/companyBasedRequest";
import "./AdminServicesModal.css";
import { Edit, Trash2, GripVertical, Store, Home, Monitor, Clock } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import BannerMessage from "../../common/bannerMessage/bannerMessage";
import AdminServiceCategoriesModal from "../AdminServiceCategoriesModal/AdminServiceCategoriesModal";

const AdminServicesModal = ({ isOpen, onClose, shopId, categories = [], initialServices = [], onOpenCategories, onSave, onSuccess }) => {
	const { language, userData, setUserData } = useContext(CombinedContext);
	const [saving, setSaving] = useState(false);
	const [invalidKeys, setInvalidKeys] = useState(new Set());
	const [bannerMessage, setBannerMessage] = useState("");
	const [activeCategoryId, setActiveCategoryId] = useState(null);
	const [showCategories, setShowCategories] = useState(false);
	const bodyRef = useRef(null);

	const categoryRefs = useRef({});
	const isProgrammaticScroll = useRef(false);
	const rowRefs = useRef({});

	const toParts = (totalMinutes = 0) => {
		const h = Math.floor((Number(totalMinutes) || 0) / 60);
		const m = (Number(totalMinutes) || 0) % 60;
		return { hours: h, minutes: m };
	};
	const toMinutes = (h = 0, m = 0) => Number(h) * 60 + Number(m);

	// Normalize any legacy boolean flag into the 3-state location model.
	const toLocationType = (s) => {
		if (s.locationType) return s.locationType;
		if (s.isHomeService) return "home";
		return "in_shop";
	};

	const [rows, setRows] = useState([]);

	useEffect(() => {
		const newRows = rows.filter((r) => r._isNew);
		const newRow = newRows[newRows.length - 1];

		if (!newRow) return;

		const el = rowRefs.current[newRow.key];

		if (el && bodyRef.current) {
			el.scrollIntoView({
				behavior: "smooth",
				block: "center",
			});
		}
	}, [rows]);

	useEffect(() => {
		const mapped = (initialServices || []).map((s, idx) => {
			const { hours, minutes } = toParts(s.duration || 0);
			return {
				key: s.serviceId || `row_${idx}_${Date.now()}`,
				serviceId: s.serviceId,
				name: s.name || s.labels?.[language] || "",
				description: s.description || "",
				hours,
				minutes,
				price: s.price ?? 0,
				currency: s.currency || "DOP",
				active: s.active !== false,
				categoryId: s.categoryId || "cat_default",
				locationType: toLocationType(s),
				transitMinutes: s.transitMinutes ?? 30,
				_markedForDelete: false,
			};
		});

		setRows(
			mapped.length
				? mapped
				: [
						{
							key: `row_${Date.now()}`,
							serviceId: null,
							name: "",
							description: "",
							hours: 0,
							minutes: 30,
							price: "",
							currency: "DOP",
							active: true,
							locationType: "in_shop",
							transitMinutes: 30,
							categoryId: categories[0]?.categoryId || "cat_default",
						},
					],
		);
	}, [initialServices, language]);

	const hourOptions = Array.from({ length: 9 }, (_, i) => i);
	const minuteOptions = [0, 15, 30, 45];

	const updateRow = (idx, patch) => {
		setRows((prev) => {
			const next = [...prev];
			next[idx] = { ...next[idx], ...patch };
			return next;
		});
	};

	const addRow = (categoryId) => {
		setRows((prev) => [
			...prev,
			{
				key: `row_${Date.now()}`,
				serviceId: null,
				name: "",
				description: "",
				hours: 0,
				minutes: 30,
				price: "",
				currency: "DOP",
				active: true,
				locationType: "in_shop",
				transitMinutes: 30,
				categoryId: categoryId || categories[0]?.categoryId || "cat_default",
				_markedForDelete: false,
				_isNew: true, // 👈 permanent until modal closes
			},
		]);
	};

	const removeRow = (idx) => {
		setRows((prev) => {
			const next = [...prev];

			// If it's a NEW row (never saved), remove immediately
			if (!next[idx].serviceId) {
				next.splice(idx, 1);
				return next;
			}

			// Existing service → mark for deletion
			next[idx] = {
				...next[idx],
				_markedForDelete: true,
			};

			return next;
		});
	};

	const getDurationMinutes = (row) => toMinutes(row.hours, row.minutes);

	const isRowValid = (row) => {
		if (!row.name?.trim()) return false;
		if (row.price === "" || row.price == null || Number(row.price) < 0) return false;
		if (getDurationMinutes(row) <= 0) return false;

		// 🔥 Transit time only required for home ("A domicilio") services
		if (row.locationType === "home") {
			if (row.transitMinutes === "" || row.transitMinutes == null || Number(row.transitMinutes) <= 0) {
				return false;
			}
		}

		return true;
	};

	useEffect(() => {
		if (!bannerMessage) return;

		const hasInvalid = rows.filter((r) => !r._markedForDelete).some((r) => !isRowValid(r));

		if (!hasInvalid) {
			setBannerMessage("");
			setInvalidKeys(new Set());
		}
	}, [rows]);

	const handleSave = async () => {
		const activeRows = rows.filter((r) => !r._markedForDelete);

		// Validate
		const invalid = activeRows.filter((r) => !isRowValid(r));

		if (invalid.length > 0) {
			setInvalidKeys(new Set(invalid.map((r) => r.key)));
			setBannerMessage(translations[language]?.profile?.errors?.fillAllFields || "Please fill in all required fields.");
			return;
		}

		setInvalidKeys(new Set()); // clear errors
		const toDelete = rows.filter((r) => r._markedForDelete && r.serviceId);

		const toSave = rows.filter((r) => !r._markedForDelete);

		const payload = toSave.map((r) => {
			const locationType = r.locationType || "in_shop";
			return {
				serviceId: r.serviceId,
				name: r.name?.trim(),
				description: r.description?.trim(),
				duration: toMinutes(r.hours, r.minutes),
				price: r.price === "" ? 0 : Number(r.price),
				currency: r.currency || "DOP",
				active: r.active !== false,
				locationType,
				// Kept for backend backward-compatibility with the old boolean flag
				isHomeService: locationType === "home",
				transitMinutes: locationType === "home" ? Number(r.transitMinutes) : 0,
				categoryId: r.categoryId || "cat_default",
			};
		});

		try {
			setSaving(true);

			// 1️⃣ Delete removed services
			for (const r of toDelete) {
				await companyBasedRequest.removeShopService(shopId, r.serviceId, userData.user.userId, userData.cognitoTokens.AccessToken);
			}

			// 2️⃣ Save remaining services
			const res = await companyBasedRequest.updateShopServices(shopId, userData.user.userId, payload, userData.cognitoTokens.AccessToken);

			setUserData((prev) => ({
				...prev,
				shop: res.shop,
			}));

			onSuccess?.(language === "es" ? "Servicios guardados exitosamente" : "Your services were saved successfully");
		} catch (e) {
			console.error("Failed to save services:", e);
		} finally {
			setSaving(false);
		}
	};

	const visibleRows = rows.filter((r) => !r._markedForDelete);

	const grouped = categories.map((cat) => ({
		cat,
		items: visibleRows.filter((r) => (r.categoryId || "cat_default") === cat.categoryId),
	}));
	useEffect(() => {
		categoryRefs.current = {};
	}, [categories]);

	useLayoutEffect(() => {
		if (!bodyRef.current) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (isProgrammaticScroll.current) return;

				const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

				if (!visible.length) return;

				const id = visible[0].target.dataset.catId;
				if (id) setActiveCategoryId(id);
			},
			{
				root: bodyRef.current,

				// 🔥 THIS IS CRITICAL
				rootMargin: "-72px 0px -60% 0px",

				// 🔥 VERY LOW threshold
				threshold: 0.01,
			},
		);

		Object.values(categoryRefs.current).forEach((el) => {
			if (el) observer.observe(el);
		});

		return () => observer.disconnect();
	}, [categories, rows.length]);

	useEffect(() => {
		if (!activeCategoryId && categories.length) {
			setActiveCategoryId(categories[0].categoryId);
		}
	}, [categories, activeCategoryId]);

	const t = translations[language]?.profile?.services || {};

	const locationOptions = [
		{ value: "in_shop", label: t.inShop || (language === "es" ? "En el local" : "In shop"), Icon: Store },
		{ value: "home", label: t.homeService || (language === "es" ? "A domicilio" : "Home service"), Icon: Home },
		{ value: "online", label: t.online || "Online", Icon: Monitor },
	];

	return (
		<div className="sf-adminsv-overlay" onClick={onClose}>
			<div className="sf-adminsv-modal" onClick={(e) => e.stopPropagation()}>
				{/* Header */}
				<div className="sf-adminsv-header">
					<h2 className="sf-adminsv-title">{t.title || "Services"}</h2>
					<button className="sf-adminsv-close" onClick={onClose} aria-label="Close">
						✕
					</button>
				</div>

				{/* Body */}
				<div className="sf-adminsv-body" ref={bodyRef}>
					<div className="sf-adminsv-cat-tabs">
						<div className="sf-adminsv-cat-tabs-inner">
							<div className={`sf-adminsv-cat-tab`} >
								{translations[language].profile.categories.categories}
							</div>
							{categories.map((cat) => (
								<button
									key={cat.categoryId}
									className={`sf-adminsv-cat-tab ${activeCategoryId === cat.categoryId ? "active" : ""}`}
									onClick={() => {
										const el = categoryRefs.current?.[cat.categoryId];
										if (!el || !bodyRef.current) return;

										isProgrammaticScroll.current = true;
										setActiveCategoryId(cat.categoryId);

										const topOffset = 72; // height of tabs
										const y =
											el.getBoundingClientRect().top -
											bodyRef.current.getBoundingClientRect().top +
											bodyRef.current.scrollTop -
											topOffset;

										bodyRef.current.scrollTo({
											top: y,
											behavior: "smooth",
										});

										setTimeout(() => {
											isProgrammaticScroll.current = false;
										}, 450);
									}}
								>
									{cat.name?.[language]}
								</button>
							))}
							<button
								className="sf-adminsv-cat-tab sf-adminsv-cat-edit"
								onClick={() => setShowCategories(true)}
								type="button"
								aria-label="Edit categories"
							>
								<Edit size={14} strokeWidth={1.8} />
							</button>
						</div>
					</div>
					<div className="sf-adminsv-sub-body">
						<DragDropContext
							onDragEnd={(result) => {
								if (!result.destination) return;

								const updated = Array.from(rows);
								const [moved] = updated.splice(result.source.index, 1);
								updated.splice(result.destination.index, 0, moved);

								setRows(updated);
							}}
						>
							<Droppable droppableId="services">
								{(provided) => (
									<div ref={provided.innerRef} {...provided.droppableProps}>
										{(() => {
											const visibleRows = rows.filter((r) => !r._markedForDelete);

											// Map row.key → real index in `rows`
											const keyToRealIndex = visibleRows.reduce((acc, r) => {
												acc[r.key] = rows.findIndex((x) => x.key === r.key);
												return acc;
											}, {});

											// Group rows by category (visual only)
											const grouped = categories.map((cat) => ({
												cat,
												items: visibleRows.filter((r) => (r.categoryId || "cat_default") === cat.categoryId),
											}));

											return grouped.map(({ cat, items }) => {
												return (
													<div
														key={cat.categoryId}
														data-cat-id={cat.categoryId}
														ref={(el) => {
															if (el) categoryRefs.current[cat.categoryId] = el;
														}}
														className="sf-adminsv-category-section"
													>
														<div className="sf-adminsv-category-header">
															<h3 className="sf-adminsv-category-title">{cat.name?.[language]}</h3>

															<button className="sf-adminsv-cat-add-btn" onClick={() => addRow(cat.categoryId)}>
																{t.add || "Add"}
															</button>
														</div>

														{items.length === 0 && <div className="sf-empty-category">{t.emptyCategory}</div>}

														{items.map((row, dndIndex) => {
															const realIdx = keyToRealIndex[row.key];

															return (
																<Draggable key={row.key} draggableId={row.key.toString()} index={dndIndex}>
																	{(dragProvided) => (
																		<div
																			className={`sf-adminsv-block
																				${invalidKeys.has(row.key) ? "sf-adminsv-invalid" : ""}
																				${row._isNew && !invalidKeys.has(row.key) ? "sf-adminsv-new" : ""}`}
																			ref={(el) => {
																				dragProvided.innerRef(el);
																				if (el) rowRefs.current[row.key] = el;
																			}}
																			{...dragProvided.draggableProps}
																		>
																			{/* Card head */}
																			<div className="sf-adminsv-card-head">
																				<span className="sf-adminsv-card-label">{t.cardLabel || "Servicio"}</span>
																				<div className="sf-adminsv-card-actions">
																					<button
																						type="button"
																						className="sf-adminsv-iconbtn sf-adminsv-remove"
																						onClick={() => removeRow(realIdx)}
																						aria-label="Delete service"
																					>
																						<Trash2 size={18} strokeWidth={1.8} />
																					</button>
																					<div
																						className="sf-adminsv-drag-handle"
																						{...dragProvided.dragHandleProps}
																						aria-label="Reorder service"
																					>
																						<GripVertical size={18} strokeWidth={1.8} />
																					</div>
																				</div>
																			</div>

																			{/* Row 1: Name + Category */}
																			<div className="sf-adminsv-row sf-adminsv-row-name">
																				<div className="sf-adminsv-field">
																					<label className="sf-adminsv-label">{t.name}</label>
																					<input
																						className="sf-adminsv-input"
																						placeholder={t.placeholderName || "Service name"}
																						value={row.name}
																						onChange={(e) => updateRow(realIdx, { name: e.target.value })}
																					/>
																				</div>

																				<div className="sf-adminsv-field">
																					<label className="sf-adminsv-label">{t.category}</label>
																					<select
																						className="sf-adminsv-select"
																						value={row.categoryId || "cat_default"}
																						onChange={(e) => updateRow(realIdx, { categoryId: e.target.value })}
																					>
																						{categories.map((cat) => (
																							<option key={cat.categoryId} value={cat.categoryId}>
																								{cat.name?.[language] || "Unnamed"}
																							</option>
																						))}
																					</select>
																				</div>
																			</div>

																			{/* Row 2: Description */}
																			<div className="sf-adminsv-field">
																				<label className="sf-adminsv-label">{t.description}</label>
																				<div className="sf-adminsv-desc-wrap">
																					<input
																						className="sf-adminsv-input sf-adminsv-input-desc"
																						placeholder={t.placeholderDescription || "Service description"}
																						value={row.description}
																						maxLength={150}
																						onChange={(e) => updateRow(realIdx, { description: e.target.value })}
																					/>
																					<span
																						className={`sf-adminsv-char-count ${(row.description?.length || 0) >= 140 ? "sf-adminsv-char-count--warn" : ""}`}
																					>
																						{row.description?.length || 0}/150
																					</span>
																				</div>
																			</div>

																			{/* Row 3: Price + Hours + Minutes */}
																			<div className="sf-adminsv-row sf-adminsv-row-meta">
																				<div className="sf-adminsv-field">
																					<label className="sf-adminsv-label">
																						{t.price} ({row.currency || "DOP"})
																					</label>
																					<input
																						className="sf-adminsv-input"
																						inputMode="decimal"
																						pattern="[0-9]*"
																						placeholder="0"
																						value={row.price}
																						onChange={(e) => {
																							const v = e.target.value.replace(/[^\d]/g, "");
																							updateRow(realIdx, { price: v });
																						}}
																					/>
																				</div>

																				<div className="sf-adminsv-field">
																					<label className="sf-adminsv-label">{t.hours}</label>
																					<select
																						className="sf-adminsv-select"
																						value={row.hours}
																						onChange={(e) => updateRow(realIdx, { hours: Number(e.target.value) })}
																					>
																						{hourOptions.map((h) => (
																							<option key={h} value={h}>
																								{h}
																							</option>
																						))}
																					</select>
																				</div>

																				<div className="sf-adminsv-field">
																					<label className="sf-adminsv-label">{t.minutes}</label>
																					<select
																						className="sf-adminsv-select"
																						value={row.minutes}
																						onChange={(e) => updateRow(realIdx, { minutes: Number(e.target.value) })}
																					>
																						{minuteOptions.map((m) => (
																							<option key={m} value={m}>
																								{m}
																							</option>
																						))}
																					</select>
																				</div>
																			</div>

																			{/* Row 4: Location */}
																			<div className="sf-adminsv-field">
																				<label className="sf-adminsv-label">
																					{t.locationLabel || (language === "es" ? "Dónde se presta" : "Where it's provided")}
																				</label>
																				<div className="sf-adminsv-loc-group">
																					{locationOptions.map(({ value, label, Icon }) => (
																						<button
																							key={value}
																							type="button"
																							className={`sf-adminsv-loc-btn ${row.locationType === value ? "active" : ""}`}
																							onClick={() => updateRow(realIdx, { locationType: value })}
																						>
																							<Icon size={18} strokeWidth={1.8} />
																							<span>{label}</span>
																						</button>
																					))}
																				</div>

																				{row.locationType === "home" && (
																					<div className="sf-adminsv-home-extra">
																						<div className="sf-adminsv-transit-info">
																							<Clock size={16} strokeWidth={1.8} />
																							<span>
																								{t.transitTimeLabel ||
																									(language === "es"
																										? "Tiempo de traslado antes del servicio (min)"
																										: "Travel time before service (min)")}
																							</span>
																						</div>
																						<input
																							className="sf-adminsv-input sf-adminsv-input-transit"
																							type="number"
																							min="0"
																							step="5"
																							value={row.transitMinutes}
																							onChange={(e) => updateRow(realIdx, { transitMinutes: e.target.value })}
																						/>
																					</div>
																				)}
																			</div>
																		</div>
																	)}
																</Draggable>
															);
														})}
													</div>
												);
											});
										})()}

										{provided.placeholder}
									</div>
								)}
							</Droppable>
						</DragDropContext>
					</div>
				</div>
				{bannerMessage && <BannerMessage type="error" text={bannerMessage} />}
				{showCategories && (
					<AdminServiceCategoriesModal
						isOpen={true}
						language={language}
						shopId={shopId}
						initialCategories={categories}
						services={rows.filter((r) => !r._markedForDelete)}
						onClose={() => setShowCategories(false)}
						onSave={(updatedCategories) => {
							setShowCategories(false);
							// don't bubble up to parent's onSave here
						}}
					/>
				)}

				{/* Footer */}
				<div className="sf-base-footer">
					<button className="btn ghost" onClick={onClose}>
						{translations[language].calendar.editor.cancel}
					</button>
					<button className="btn" onClick={handleSave} disabled={saving}>
						{saving ? translations[language].calendar.editor.saving : translations[language].calendar.editor.save}
					</button>
				</div>
			</div>
		</div>
	);
};

export default AdminServicesModal;